import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, UsersIcon, ShieldCheckIcon, 
  ChartBarIcon, CogIcon, X, 
  MessageSquare, MessageCircle, Package, ChevronDown
} from 'lucide-react';

function Sidebar({ isMobileMenuOpen, onClose }) {
  const location = useLocation();

  const navItems = [
    { name: 'Dashboard', path: '/', icon: HomeIcon },
    { name: 'Users', path: '/users', icon: UsersIcon },
    { 
      name: 'Content Moderation', 
      path: '/moderation', 
      icon: ShieldCheckIcon,
      children: [
        { name: 'All Reports', path: '/moderation/all', icon: ShieldCheckIcon },
        { name: 'Flagged Messages', path: '/moderation/messages', icon: MessageCircle },
        { name: 'Flagged GoGets', path: '/moderation/gogets', icon: Package }
      ]
    },
    { name: 'Message Filter', path: '/message-filter', icon: MessageSquare },
    { name: 'Analytics', path: '/analytics', icon: ChartBarIcon },
    { name: 'Settings', path: '/settings', icon: CogIcon }
  ];


    // Check if path or any of its children are active
    const isActive = (item) => {
      if (location.pathname === item.path) return true;
      if (item.children) {
        return item.children.some(child => location.pathname === child.path);
      }
      return false;
    };
  
    // Check if item should be expanded (for items with children)
    const isExpanded = (item) => {
      if (!item.children) return false;
      return item.children.some(child => location.pathname === child.path) || 
             location.pathname === item.path;
    };
  
    // State to track manually expanded items
    const [expandedItems, setExpandedItems] = React.useState({});
  
    const toggleExpand = (itemName, e) => {
      e.preventDefault();
      setExpandedItems(prev => ({
        ...prev,
        [itemName]: !prev[itemName]
      }));
    };
  
    const shouldShowChildren = (item) => {
      return expandedItems[item.name] || isExpanded(item);
    };
  

  return (
    <>
      {/* Mobile Close Button */}
      <button 
        onClick={onClose}
        className={`
          fixed top-4 left-4 z-50 
          ${isMobileMenuOpen ? 'block' : 'hidden'} 
          md:hidden 
          bg-white p-2 rounded-lg shadow-md
        `}
      >
        <X className="h-6 w-6" />
      </button>

      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 h-screen w-64 
          bg-gradient-to-br from-gray-50 to-gray-100 
          border-r border-gray-200 pt-16 
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 z-40
        `}
      >
        <div className="flex flex-col h-full">
          <nav className="flex-1 px-4 pb-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              
              return (
                <div key={item.path}>
                  {/* Parent menu item */}
                  <Link
                    to={item.path}
                    onClick={(e) => hasChildren ? toggleExpand(item.name, e) : onClose()}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors shadow-sm ${
                      isActive(item)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    {hasChildren && (
                      <ChevronDown className={`h-4 w-4 transition-transform ${shouldShowChildren(item) ? 'rotate-180' : ''}`} />
                    )}
                  </Link>

                  {/* Children menu items */}
                  {hasChildren && shouldShowChildren(item) && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.children.map(child => {
                        const ChildIcon = child.icon;
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={onClose}
                            className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                              location.pathname === child.path
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <ChildIcon className="h-4 w-4" />
                            <span className="text-sm">{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="px-4 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">GoGet Admin v1.0</p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div 
          onClick={onClose}
          className="fixed inset-0 bg-black opacity-50 z-30 md:hidden"
        />
      )}
    </>
  );
}

export default Sidebar;